const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4003' : '');

// ── Types ──────────────────────────────────────────────────────────────────

export type OrderStatus = 'kyc_required' | 'kyc_pending' | 'ready_to_pay' | 'invoice_created' | 'paid' | 'processing' | 'completed' | 'failed' | 'expired';

export interface ApiOrder {
  id: string;
  rub: number;
  usd: number;
  uid: string;
  providerCode?: string | null;
  name: string | null;
  promo: string | null;
  status: OrderStatus;
  ref: string | null;
  transferId: string | null;
  paymentClientId: string | null;
  paymentUuid: string | null;
  paymentQrUrl: string | null;
  paymentDeepLink: string | null;
  paymentSbpQr: string | null;
  paidAt: string | null;
  fulfilledAt: string | null;
  createdAt: string;
}

export interface UserProfile {
  id: string;
  email: string;
  paymentClientId: string;
  bingxUid: string | null;
  bingxUidLocked: boolean;
  promoCode: string;
  partnerRef: string | null;
  kycStatus: string;
  isSbpVerified: boolean;
  kycUpdatedAt: string | null;
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  birthDate?: string | null;
  phone?: string | null;
}

export interface UserAuthResponse {
  token: string;
  user: UserProfile;
}

export interface StartKycResponse {
  status: string;
  kycUrl?: string | null;
}

export interface PublicSettings {
  liquidity: number;
  turnover: number;
  rate: number;
  commissionDefault: number;
  commissionPromo: number;
  maintenanceMode: boolean;
}

export type UsdtNetwork = 'TRC20' | 'BEP20' | 'TONUSDT';

export interface PaymentProduct {
  asset: string;
  network: UsdtNetwork | string;
  destinationType?: string;
  liquidity?: number | null;
  available_liquidity?: number | null;
  availableLiquidity?: number | null;
  liquidity_error?: string | null;
  liquidityError?: string | null;
}


export interface PaymentStatus {
  transactionId: string | null;
  status: string;
  qrLink: string | null;
  deepLink: string | null;
  sbpQr: string | null;
}

export interface PaymentCalculation {
  amountRub: number;
  bitbankerFeePercent: number;
  bitbankerFeeRub: number;
  platformFeePercent: number;
  platformFeeRub: number;
  netRub: number;
  rubrUsdtRate: number;
  usdtAmount: number;
  assetAmount?: number;
  asset?: string;
}

export interface PaymentLimits {
  minAmount: number;
  maxAmount: number;
}

export interface OrdersPage {
  items: ApiOrder[];
  total: number;
  page: number;
  limit: number;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Ошибка HTTP ${res.status}`);
  }
  return data as T;
}

// ── Public API ─────────────────────────────────────────────────────────────

export function getPublicSettings(params?: { network?: UsdtNetwork }): Promise<PublicSettings> {
  const qs = new URLSearchParams();
  const network = params?.network ?? 'BEP20';
  qs.set('network', network === 'TONUSDT' ? 'BEP20' : network);
  const query = qs.toString();
  const settingsPath = `/api/settings/public${query ? `?${query}` : ''}`;
  return Promise.allSettled([
    request<PublicSettings>(settingsPath),
    getPaymentProducts(),
  ]).then(([settingsResult, productsResult]) => {
    if (settingsResult.status === 'rejected' && productsResult.status === 'rejected') {
      throw settingsResult.reason;
    }

    const settings = settingsResult.status === 'fulfilled'
      ? settingsResult.value
      : { liquidity: 0, turnover: 0, rate: 90, commissionDefault: 0.09, commissionPromo: 0.07, maintenanceMode: false };
    if (productsResult.status !== 'fulfilled') return settings;

    const product = productsResult.value.find(item =>
      item.asset?.toUpperCase() === 'USDT' &&
      item.network?.replace(/[^a-z0-9]/gi, '').toUpperCase() === network
    );
    const liquidity = product == null ? null : product.available_liquidity
      ?? product.availableLiquidity
      ?? product.liquidity
      ?? null;

    return typeof liquidity === 'number' && Number.isFinite(liquidity)
      ? { ...settings, liquidity }
      : settings;
  });
}

export async function getPaymentProducts(): Promise<PaymentProduct[]> {
  try {
    return await request<PaymentProduct[]>('/api/payments/products');
  } catch (productsError) {
    try {
      return await request<PaymentProduct[]>('/api/external/payments/products');
    } catch {
      throw productsError;
    }
  }
}

export function createOrder(req: {
    rub: number;
    network: UsdtNetwork;
    uid?: string;
  }, token: string): Promise<ApiOrder> {
  return request<ApiOrder>('/api/orders', {
    method: 'POST',
    body: JSON.stringify(req),
  }, token);
}

export function calculateOrderPayment(req: {
    rub: number;
    network: UsdtNetwork;
  }, token: string): Promise<PaymentCalculation> {
  return request<PaymentCalculation>('/api/orders/calculate', {
    method: 'POST',
    body: JSON.stringify(req),
  }, token);
}

export function calculatePublicOrderPayment(req: {
  rub: number;
  network: UsdtNetwork;
}): Promise<PaymentCalculation> {
  return request<PaymentCalculation>('/api/public/orders/calculate', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export function getOrderPaymentLimits(token: string): Promise<PaymentLimits> {
  return request<PaymentLimits>('/api/orders/limits', {}, token);
}

export function getPublicOrderPaymentLimits(): Promise<PaymentLimits> {
  return request<PaymentLimits>('/api/public/orders/limits');
}

export function getOrderPayment(orderId: string): Promise<PaymentStatus> {
  return request<PaymentStatus>(`/api/orders/${orderId}/payment`);
}

export function getOrder(orderId: string, token: string): Promise<ApiOrder> {
  return request<ApiOrder>(`/api/orders/${orderId}`, {}, token);
}

export function getUserOrders(
  token: string,
  params?: { page?: number; limit?: number },
): Promise<OrdersPage> {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  qs.set('limit', String(params?.limit ?? 20));
  return request<OrdersPage>(`/api/user/orders?${qs}`, {}, token);
}

export function registerUser(email: string, password: string, promoCode: string): Promise<UserAuthResponse> {
  return request<UserAuthResponse>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, promoCode }),
  });
}

export function userLogin(email: string, password: string): Promise<UserAuthResponse> {
  return request<UserAuthResponse>('/api/auth/user/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function getMe(token: string): Promise<UserProfile> {
  return request<UserProfile>('/api/user/me', {}, token);
}

export interface UpdateUserProfileRequest {
  bingxUid?: string;
  lastName: string;
  firstName: string;
  middleName?: string;
  birthDate: string;
  phone: string;
}

export function updateUserProfile(token: string, profile: UpdateUserProfileRequest): Promise<UserProfile> {
  return request<UserProfile>('/api/user/profile', {
    method: 'PUT',
    body: JSON.stringify(profile),
  }, token);
}

export function requestPasswordRecovery(email: string): Promise<void> {
  return request<void>('/api/auth/user/password-recovery', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export function changeUserPassword(token: string, currentPassword: string, newPassword: string): Promise<void> {
  return request<void>('/api/user/password', {
    method: 'PUT',
    body: JSON.stringify({ currentPassword, newPassword }),
  }, token);
}

export function startKyc(token: string): Promise<StartKycResponse> {
  return request<StartKycResponse>('/api/client/kyc/start', {
    method: 'POST',
  }, token);
}

export function refreshKyc(token: string): Promise<UserProfile> {
  return request<UserProfile>('/api/user/kyc/status', {}, token);
}

export function validatePromo(code: string): Promise<{ valid: boolean; discountRate: number | null }> {
  return request<{ valid: boolean; discountRate: number | null }>('/api/promo/validate', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}



